import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, TextField, Chip, FormControlLabel, Switch, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { licensesAPI } from '../services/api';

const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');
const ACTION = { added: 'success', changed: 'info', removed: 'warning', imported: 'default' };

function LicenseDialog({ customer, license, products, onClose, onSaved }) {
  const [form, setForm] = useState({
    product_name: license?.product_name || '', product_key: license?.product_key || '', quantity: license?.quantity ?? 1,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const data = { ...form, quantity: form.quantity === '' ? null : Number(form.quantity) };
      if (license) await licensesAPI.update(license.id, data); else await licensesAPI.add(customer.id, data);
      onSaved(license ? 'Licence updated' : 'Licence added');
    } catch (err) { setError(errText(err, 'Could not save the licence')); setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{license ? 'Edit licence' : 'Add licence'} - {customer.cust_code}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Autocomplete freeSolo options={products} inputValue={form.product_name}
            onInputChange={(_, v) => setForm((f) => ({ ...f, product_name: v }))}
            renderInput={(params) => <TextField {...params} label="Product name" required />} />
          <TextField label="Product key" required value={form.product_key} onChange={(e) => setForm({ ...form, product_key: e.target.value })} />
          <TextField label="Quantity" type="number" required value={form.quantity} slotProps={{ htmlInput: { min: 0 } }}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>{busy ? 'Saving...' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveDialog({ license, onClose, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const remove = async () => {
    setBusy(true);
    try { await licensesAPI.remove(license.id); onRemoved(); } catch (err) { setError(errText(err, 'Could not remove the licence')); setBusy(false); }
  };
  return (
    <Dialog open onClose={() => !busy && onClose()}>
      <DialogTitle>Remove licence?</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography>{license.product_name} ({license.product_key}), quantity {license.quantity}, will be removed from the list. It stays in the history.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button color="error" variant="contained" onClick={remove} disabled={busy}>Remove</Button>
      </DialogActions>
    </Dialog>
  );
}

function CustomerLicenses() {
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [products, setProducts] = useState([]);
  const [dialog, setDialog] = useState(null);       // { type: 'add' } | { type: 'edit', license } | { type: 'remove', license }
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadCustomers = useCallback(async () => {
    try {
      const res = await licensesAPI.customers(search.trim(), showAll);
      setCustomers(res.data); setError(null);
    } catch (err) { setError(errText(err, 'Could not load customers')); setCustomers([]); }
  }, [search, showAll]);

  const loadDetail = useCallback(async (customer) => {
    if (!customer) { setDetail(null); return; }
    try { setDetail((await licensesAPI.forCustomer(customer.id)).data); }
    catch (err) { setError(errText(err, 'Could not load the licences')); setDetail({ licenses: [], history: [] }); }
  }, []);

  useEffect(() => { const t = setTimeout(loadCustomers, 250); return () => clearTimeout(t); }, [loadCustomers]);
  useEffect(() => { loadDetail(selected); }, [selected, loadDetail]);
  useEffect(() => { licensesAPI.products().then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const changed = (message) => {
    setDialog(null); setSuccess(message);
    loadDetail(selected); loadCustomers();
    licensesAPI.products().then((r) => setProducts(r.data)).catch(() => {});
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Customer Licenses</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Product keys and quantities for each customer. Every addition, change and removal is kept in the history.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Paper variant="outlined" sx={{ flex: '1 1 360px', minWidth: 0, p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
            <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 160 }} />
            <FormControlLabel control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />} label="All customers" />
          </Box>
          {customers === null ? <CircularProgress size={24} /> : (
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>CL Number</TableCell><TableCell>Code</TableCell><TableCell>Customer</TableCell><TableCell align="right">Licenses</TableCell></TableRow></TableHead>
                <TableBody>
                  {customers.map((c) => (
                    <TableRow key={c.id} hover selected={selected?.id === c.id} onClick={() => setSelected(c)} sx={{ cursor: 'pointer' }}>
                      <TableCell>{c.cl_number || '-'}</TableCell><TableCell>{c.cust_code}</TableCell><TableCell>{c.cust_name}</TableCell>
                      <TableCell align="right">{c.licenses}</TableCell>
                    </TableRow>
                  ))}
                  {customers.length === 0 && <TableRow><TableCell colSpan={4}><Typography color="text.secondary">No customers found. Turn on "All customers" to add the first licence for a customer.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ flex: '2 1 480px', minWidth: 0, p: 2 }}>
          {!selected ? <Typography color="text.secondary">Select a customer to see their licenses.</Typography> : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6">{selected.cust_code} - {selected.cust_name}</Typography>
                <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => setDialog({ type: 'add' })}>Add license</Button>
              </Box>
              {detail === null ? <CircularProgress size={24} /> : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Product</TableCell><TableCell>Product key</TableCell><TableCell align="right">Qty</TableCell><TableCell /></TableRow></TableHead>
                      <TableBody>
                        {detail.licenses.map((l) => (
                          <TableRow key={l.id}>
                            <TableCell>{l.product_name}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{l.product_key}</TableCell>
                            <TableCell align="right">{l.quantity}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title="Edit"><IconButton size="small" aria-label={`Edit ${l.product_key}`} onClick={() => setDialog({ type: 'edit', license: l })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Remove"><IconButton size="small" aria-label={`Remove ${l.product_key}`} onClick={() => setDialog({ type: 'remove', license: l })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {detail.licenses.length === 0 && <TableRow><TableCell colSpan={4}><Typography color="text.secondary">No licenses recorded for this customer.</Typography></TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>History</Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow><TableCell>When</TableCell><TableCell>Action</TableCell><TableCell>Product</TableCell><TableCell>Detail</TableCell><TableCell>By</TableCell></TableRow></TableHead>
                      <TableBody>
                        {detail.history.map((h) => (
                          <TableRow key={h.id}>
                            <TableCell>{fmtTime(h.changed_at)}</TableCell>
                            <TableCell><Chip size="small" label={h.action} color={ACTION[h.action] || 'default'} /></TableCell>
                            <TableCell>{h.product_name}<Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>{h.product_key}</Typography></TableCell>
                            <TableCell>{h.detail || '-'}</TableCell>
                            <TableCell>{h.changed_by}</TableCell>
                          </TableRow>
                        ))}
                        {detail.history.length === 0 && <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No history yet.</Typography></TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </Paper>
      </Box>

      {dialog?.type === 'add' && <LicenseDialog customer={selected} products={products} onClose={() => setDialog(null)} onSaved={changed} />}
      {dialog?.type === 'edit' && <LicenseDialog customer={selected} license={dialog.license} products={products} onClose={() => setDialog(null)} onSaved={changed} />}
      {dialog?.type === 'remove' && <RemoveDialog license={dialog.license} onClose={() => setDialog(null)} onRemoved={() => changed('Licence removed')} />}
    </Box>
  );
}

export default CustomerLicenses;
